import { useStore } from '../store/useStore'
import { P5_VERSIONS } from '../config/p5versions'
import { 
  FileCode, 
  FileText, 
  FileType,
  AlertTriangle,
  AlertCircle,
  Activity
} from 'lucide-react'

export default function StatusBar() {
  const { 
    currentFile, 
    files,
    p5Version,
    setP5Version,
    showConsole,
    toggleConsole,
    consoleMessages,
    participantId
  } = useStore()

  const p5Versions = P5_VERSIONS

  // Get file extension icon
  const getFileIcon = () => {
    if (!currentFile) return FileText
    if (currentFile.endsWith('.js')) return FileCode
    if (currentFile.endsWith('.html')) return FileType
    return FileText
  }

  // Count errors and warnings in console
  const errorCount = consoleMessages?.filter(m => m.level === 'error').length || 0
  const warnCount = consoleMessages?.filter(m => m.level === 'warn').length || 0

  const FileIcon = getFileIcon()

  return (
    <div className="h-6 flex items-center justify-between px-3 bg-ide-surface border-t border-ide-border text-xs">
      {/* Left section */}
      <div className="flex items-center gap-4">
        {/* Current file */}
        <div className="flex items-center gap-1.5 text-ide-textMuted">
          <FileIcon size={12} />
          <span>{currentFile || 'No file selected'}</span>
        </div>

        {/* Error/Warning count - clickable to open console */}
        <button
          onClick={toggleConsole}
          className={`flex items-center gap-2 px-1.5 py-0.5 rounded hover:bg-ide-surfaceHover transition-colors ${
            errorCount > 0 ? 'text-ide-error' : warnCount > 0 ? 'text-ide-warning' : 'text-ide-textDim'
          }`}
          title={showConsole ? 'Hide Console' : 'Show Console'}
        >
          {errorCount > 0 && (
            <span className="flex items-center gap-1">
              <AlertCircle size={12} />
              <span>{errorCount}</span>
            </span>
          )}
          {warnCount > 0 && (
            <span className="flex items-center gap-1">
              <AlertTriangle size={12} />
              <span>{warnCount}</span>
            </span>
          )}
          {errorCount === 0 && warnCount === 0 && (
            <span className="text-ide-textDim">No issues</span>
          )}
        </button>
      </div>

      {/* Right section */}
      <div className="flex items-center gap-4">
        {/* Research session indicator */}
        {participantId && (
          <div className="flex items-center gap-1.5 text-ide-textDim" title="Research session active">
            <Activity size={10} className="text-ide-success animate-pulse" />
            <span>Session</span>
          </div>
        )}

        {/* p5.js version selector */}
        <select
          value={p5Version}
          onChange={(e) => setP5Version(e.target.value)}
          className="bg-transparent text-ide-textMuted hover:text-ide-text cursor-pointer outline-none"
          title="p5.js Version"
        >
          {p5Versions.map(v => (
            <option key={v} value={v} className="bg-ide-surface">p5.js v{v}</option>
          ))}
        </select>
      </div>
    </div>
  )
}
